/**
 * Director's Monitor — Cinematic progress display for long-running tasks.
 *
 * Instead of a bland progress bar, shows:
 * - Checkpoint steps that light up as each stage completes
 * - Rotating film quotes + system status commentary
 * - Desaturated cinematography backdrop
 * - Persistent notification when task finishes in background
 *
 * Usage:
 *   <DirectorMonitor
 *     steps={[
 *       { key: "analyze", label: "Analyzing Script" },
 *       { key: "generate", label: "Generating Shots" },
 *       { key: "render", label: "Rendering Frames" },
 *     ]}
 *     currentStep="generate"
 *     status="active"         // "active" | "completed" | "failed" | "cancelled"
 *     commentary="Crafting visual prompts for Scene 3..."
 *     onDismiss={() => {}}
 *     compact={false}         // true = inline toast, false = overlay panel
 *   />
 */
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Circle, Loader2, X, AlertTriangle, Clapperboard } from "lucide-react";

// Curated film quotes by task context
const FILM_QUOTES = {
  script: [
    { quote: "The most honest form of filmmaking is to make a film for yourself.", author: "Peter Jackson" },
    { quote: "If it can be written, or thought, it can be filmed.", author: "Stanley Kubrick" },
    { quote: "A film is — or should be — more like music than like fiction.", author: "Stanley Kubrick" },
    { quote: "Every great film should seem new every time you see it.", author: "Roger Ebert" },
    { quote: "Cinema is a matter of what's in the frame and what's out.", author: "Martin Scorsese" },
  ],
  shots: [
    { quote: "In the right light, at the right time, everything is extraordinary.", author: "Aaron Rose" },
    { quote: "A shot can change the meaning of everything.", author: "Sergei Eisenstein" },
    { quote: "Photography is truth. The cinema is truth twenty-four times per second.", author: "Jean-Luc Godard" },
    { quote: "Light makes photography. Embrace light. Admire it. Love it.", author: "George Eastman" },
    { quote: "There are no rules in filmmaking — only sins. And the cardinal sin is dullness.", author: "Frank Capra" },
  ],
  characters: [
    { quote: "What all great characters have in common is that they want something.", author: "Robert McKee" },
    { quote: "Every saint has a past, every sinner has a future.", author: "Oscar Wilde" },
    { quote: "Acting is behaving truthfully under imaginary circumstances.", author: "Sanford Meisner" },
    { quote: "A character is what he does in the dark.", author: "John Whiting" },
    { quote: "The best thing about a picture is that it can't change.", author: "Wim Wenders" },
  ],
  general: [
    { quote: "The cinema has no boundary; it is a ribbon of dream.", author: "Orson Welles" },
    { quote: "Film is a battleground: love, hate, action, violence, death.", author: "Samuel Fuller" },
    { quote: "All you need for a movie is a gun and a girl.", author: "Jean-Luc Godard" },
    { quote: "I don't dream at night, I dream all day — I dream for a living.", author: "Steven Spielberg" },
    { quote: "Every film is its own experience, its own world.", author: "Denis Villeneuve" },
    { quote: "The length of a film should be directly related to the endurance of the human bladder.", author: "Alfred Hitchcock" },
  ],
};

function getQuote(context = "general") {
  const pool = FILM_QUOTES[context] || FILM_QUOTES.general;
  return pool[Math.floor(Math.random() * pool.length)];
}

export default function DirectorMonitor({
  steps = [],
  currentStep = "",
  status = "active",
  commentary = "",
  errorMessage = "",
  quoteContext = "general",
  onDismiss,
  compact = false,
}) {
  const [quote, setQuote] = useState(() => getQuote(quoteContext));
  const [showQuote, setShowQuote] = useState(true);
  const quoteTimer = useRef(null);

  // Rotate quotes every 8 seconds
  useEffect(() => {
    quoteTimer.current = setInterval(() => {
      setShowQuote(false);
      setTimeout(() => {
        setQuote(getQuote(quoteContext));
        setShowQuote(true);
      }, 400);
    }, 8000);
    return () => clearInterval(quoteTimer.current);
  }, [quoteContext]);

  const isActive = status === "active";
  const isDone = status === "completed";
  const isFailed = status === "failed";

  // Find current step index
  const currentIdx = steps.findIndex(s => s.key === currentStep);

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
        className="fixed bottom-6 right-6 z-50 w-[420px] rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-2xl overflow-hidden"
      >
        {/* Cinematic top accent */}
        <div className={`h-1 ${isDone ? 'bg-green-500' : isFailed ? 'bg-red-500' : 'bg-gradient-to-r from-[var(--color-gold-500)] via-[var(--color-gold-400)] to-[var(--color-gold-500)]'}`}>
          {isActive && (
            <motion.div
              className="h-full w-1/3 bg-white/20"
              animate={{ x: ["-100%", "400%"] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            />
          )}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              isDone ? 'bg-green-500/10' : isFailed ? 'bg-red-500/10' : 'bg-[var(--color-gold-500)]/10'
            }`}>
              {isActive && <Loader2 size={16} className="animate-spin text-[var(--color-gold-400)]" />}
              {isDone && <CheckCircle2 size={16} className="text-green-500" />}
              {isFailed && <AlertTriangle size={16} className="text-red-500" />}
              {status === "cancelled" && <X size={16} className="text-[var(--text-muted)]" />}
            </div>
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">
                {isDone ? "Complete" : isFailed ? "Failed" : status === "cancelled" ? "Cancelled" : "Working..."}
              </p>
              <p className="text-[11px] text-[var(--text-muted)]">{commentary}</p>
            </div>
          </div>
          {!isActive && onDismiss && (
            <button onClick={onDismiss} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] p-1">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Checkpoints */}
        <div className="px-4 pb-3">
          <div className="flex items-center gap-1">
            {steps.map((step, i) => {
              const isComplete = i < currentIdx || isDone;
              const isCurrent = i === currentIdx && isActive;
              const isPending = i > currentIdx && !isDone;

              return (
                <div key={step.key} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div className={`w-full h-1.5 rounded-full transition-all duration-500 ${
                      isComplete ? 'bg-[var(--color-gold-500)]'
                      : isCurrent ? 'bg-[var(--color-gold-500)]/40'
                      : 'bg-[var(--bg-muted)]'
                    }`}>
                      {isCurrent && (
                        <motion.div
                          className="h-full rounded-full bg-[var(--color-gold-500)]"
                          initial={{ width: "10%" }}
                          animate={{ width: "70%" }}
                          transition={{ duration: 30, ease: "linear" }}
                        />
                      )}
                    </div>
                    <span className={`text-[9px] mt-1 truncate max-w-full text-center ${
                      isComplete || isCurrent ? 'text-[var(--color-gold-400)]' : 'text-[var(--text-disabled)]'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Error message */}
        {isFailed && errorMessage && (
          <div className="px-4 pb-3">
            <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-[11px] text-red-400 leading-relaxed">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Quote */}
        {isActive && (
          <div className="px-4 pb-3">
            <AnimatePresence mode="wait">
              {showQuote && (
                <motion.div
                  key={quote.quote}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-[10px] text-[var(--text-disabled)] italic leading-relaxed">
                    "{quote.quote}"
                    <span className="not-italic ml-1.5 text-[var(--text-disabled)]/60">— {quote.author}</span>
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </motion.div>
    );
  }

  // Full overlay version (for initial page loads, etc.)
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="max-w-md w-full px-8 py-12 text-center">
        {/* Clapperboard icon */}
        <motion.div
          initial={{ rotate: -5, opacity: 0 }}
          animate={{ rotate: 0, opacity: 1 }}
          className="mb-8"
        >
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[var(--color-gold-500)]/10 flex items-center justify-center">
            {isActive && <Clapperboard size={28} className="text-[var(--color-gold-400)]" />}
            {isDone && <CheckCircle2 size={28} className="text-green-500" />}
            {isFailed && <AlertTriangle size={28} className="text-red-500" />}
          </div>
        </motion.div>

        {/* Commentary */}
        <p className="text-sm text-[var(--text-primary)] font-medium mb-2">
          {isDone ? "All done!" : isFailed ? "Something went wrong" : commentary || "Processing..."}
        </p>

        {/* Checkpoint steps */}
        <div className="space-y-3 my-8 text-left">
          {steps.map((step, i) => {
            const isComplete = i < currentIdx || isDone;
            const isCurrent = i === currentIdx && isActive;

            return (
              <motion.div
                key={step.key}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3"
              >
                {isComplete ? (
                  <CheckCircle2 size={18} className="text-[var(--color-gold-500)] flex-shrink-0" />
                ) : isCurrent ? (
                  <Loader2 size={18} className="animate-spin text-[var(--color-gold-400)] flex-shrink-0" />
                ) : (
                  <Circle size={18} className="text-[var(--text-disabled)] flex-shrink-0" />
                )}
                <span className={`text-sm ${
                  isComplete ? 'text-[var(--text-primary)]'
                  : isCurrent ? 'text-[var(--color-gold-400)] font-medium'
                  : 'text-[var(--text-disabled)]'
                }`}>
                  {step.label}
                </span>
              </motion.div>
            );
          })}
        </div>

        {/* Error message */}
        {isFailed && errorMessage && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-6 text-left">
            <p className="text-xs text-red-400 leading-relaxed">{errorMessage}</p>
          </div>
        )}

        {/* Quote */}
        {isActive && (
          <AnimatePresence mode="wait">
            {showQuote && (
              <motion.div
                key={quote.quote}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.4 }}
                className="mt-4"
              >
                <p className="text-xs text-[var(--text-muted)] italic leading-relaxed">
                  "{quote.quote}"
                </p>
                <p className="text-[10px] text-[var(--text-disabled)] mt-1">— {quote.author}</p>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}

// Export quote collections for use by other components
export { FILM_QUOTES, getQuote };
